import { Check, Loader2, AlertCircle, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { Note } from '@/types'
import { Button } from '@/components/ui/button'

export interface NoteProgress {
  stage: string
  current: number
  total: number
  lastUpdateTime: number
}

export interface NoteCardProps {
  note: Note
  selected: boolean
  onSelect: () => void
  onClick: () => void
  onAbort?: (noteId: string) => void
  isWaiting?: boolean
  isRecentlyCompleted?: boolean
  progress?: NoteProgress
}

/**
 * 笔记卡片组件
 * 用于瀑布流展示笔记
 */
export function NoteCard({
  note,
  selected,
  onSelect,
  onClick,
  onAbort,
  isWaiting,
  isRecentlyCompleted,
  progress,
}: NoteCardProps) {
  const cover = note.images[0]?.url
  const progressPercent = progress
    ? Math.round((progress.current / progress.total) * 100)
    : 0
  const timeSinceUpdate = progress ? Date.now() - progress.lastUpdateTime : 0
  const isStuck = timeSinceUpdate > 180000 // 阈值提高到3分钟，与心跳检测保持一致

  return (
    <div
      className={cn(
        'group relative bg-white rounded-xl overflow-hidden border shadow-sm transition-all hover:shadow-md cursor-pointer',
        selected && 'ring-2 ring-primary border-primary'
      )}
      onClick={onClick}
    >
      {/* 选择勾选框 (阻止冒泡) */}
      <div
        className="absolute top-2 right-2 z-10"
        onClick={e => {
          e.stopPropagation()
          onSelect()
        }}
      >
        <div
          className={cn(
            'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors bg-white/80 backdrop-blur',
            selected
              ? 'border-primary bg-primary text-white'
              : 'border-gray-300 hover:border-primary'
          )}
        >
          {selected && <Check className="w-3 h-3" />}
        </div>
      </div>

      {/* 封面图 - 固定 3:4 比例 */}
      <div className="w-full relative bg-gray-100 aspect-[3/4]">
        {cover ? (
          <img
            src={cover}
            alt={note.title}
            className="w-full h-full object-cover block"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
            {isWaiting
              ? '等待中'
              : note.status === 'planning'
                ? '方案规划中'
                : note.status === 'generating'
                  ? '配图生成中'
                  : '无配图'}
          </div>
        )}

        {/* 等待中状态 */}
        {isWaiting && (
          <Badge
            className="absolute left-2 top-2 bg-gray-500/70 text-white backdrop-blur"
            variant="secondary"
          >
            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> 等待中
          </Badge>
        )}

        {/* 生成中状态 - 显示详细进度 */}
        {!isWaiting &&
          (note.status === 'planning' || note.status === 'generating') && (
            <div className="absolute left-2 top-2 right-10 space-y-1 z-20">
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 bg-black/70 hover:bg-black/90 text-white backdrop-blur border-none rounded-md shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAbort?.(note.id)
                  }}
                  title="中止生成"
                >
                  <XCircle className="w-4 h-4 text-red-400" />
                </Button>
                <Badge
                  className={cn(
                    'flex-1 justify-start backdrop-blur h-7 truncate',
                    isStuck ? 'bg-orange-500/80' : 'bg-black/70',
                    'text-white'
                  )}
                  variant="secondary"
                >
                  {isStuck ? (
                    <>
                      <AlertCircle className="w-3 h-3 mr-1" /> 超时
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />{' '}
                      {note.status === 'planning'
                        ? '规划中'
                        : `配图 ${progress?.current || 0}/${progress?.total || 0}`}
                    </>
                  )}
                </Badge>
              </div>
              {progress && note.status === 'generating' && (
                <div className="bg-black/60 backdrop-blur rounded-full h-1.5 overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all duration-300',
                      isStuck ? 'bg-orange-400' : 'bg-green-400'
                    )}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              )}
            </div>
          )}

        {/* 最近完成标记 */}
        {isRecentlyCompleted &&
          !isWaiting &&
          note.status !== 'planning' &&
          note.status !== 'generating' && (
            <Badge
              className="absolute left-2 top-2 bg-green-500/80 text-white backdrop-blur"
              variant="secondary"
            >
              ✓ 完成
            </Badge>
          )}

        <div className="absolute bottom-2 left-2">
          <Badge
            variant="secondary"
            className="backdrop-blur bg-white/70 text-[10px] px-1.5 h-5"
          >
            {note.style}
          </Badge>
        </div>
      </div>

      {/* 内容摘要 */}
      <div className="p-3 space-y-2">
        <h3 className="font-semibold text-sm leading-tight line-clamp-2">
          {note.title}
        </h3>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{new Date(note.created_at).toLocaleDateString()}</span>
          <span>{note.images.length}图</span>
        </div>
      </div>
    </div>
  )
}

